import axios from 'axios';
import {
  LoginData,
  GarageRegistration,
  ResetPasswordData,
  AuthResponse,
  User,
  PaymentData,
  GarageUser, GarageProfileUpdateData
} from '../types/auth';
import { API_URL } from '../config/api.config';

interface ChangePasswordRequest {
  userId: string;
  currentPassword: string;
  newPassword: string;
}
const getAuthInfo = () => {
  const garageId = JSON.parse(localStorage.getItem('user') || '{}')._id || ''
  const token = localStorage.getItem('token');
  return { garageId, token };
};


const createAuthAxios = () => {
  const { token } = getAuthInfo();

  return axios.create({
    baseURL: API_URL,
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
};
const authAxios = createAuthAxios()
class AuthService {
  private user: User | GarageUser | null = null;


  async changePassword(userId: string, currentPassword: string, newPassword: string): Promise<any> {
    try {
      const { token } = getAuthInfo();
      if (token) {
        const response = await authAxios.post(`${API_URL}/auth/change-password`, { userId, currentPassword, newPassword });
        console.log(response);
        return response.data;
      } else {
        throw new Error('Non authentifié');
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Mot de passe actuel incorrect');
      } else {
        throw new Error(error.response?.data?.message || 'Erreur lors du changement de mot de passe');
      }
    }
  }
  async login(data: LoginData): Promise<AuthResponse> {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, data);
      // ⭐ GESTION SIMPLE : Si email non vérifié, le backend a déjà envoyé l'email
      if (response.data.emailNotVerified && response.data.emailSent) {
        // On ne stocke rien, on retourne juste l'info
        return {
          ...response.data,
          emailNotVerified: true,
          emailSent: true
        };
      }

      // Connexion normale réussie
      if (response.data.user && response.data.token) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('refreshToken', response.data.refreshToken);
        this.user = response.data.user;
      }

      return response.data;
    } catch (error: any) {
      if (error.response?.status === 403 && error.response?.data?.emailNotVerified) {
        // Le backend a renvoyé un 403 avec les infos d'email non vérifié
        return {
          message: error.response.data.message,
          emailNotVerified: true,
          emailSent: error.response.data.emailSent || false,
          ...error.response.data,
        };
      }

      // Autres erreurs (404, 401, 500, etc.)
      throw new Error(error.response?.data?.message || 'Email ou mot de passe incorrect');
    }
  }

  /**
   * Méthode pour préparer le paiement sans créer l'utilisateur
   * Cette nouvelle méthode prépare uniquement le setup intent pour Stripe
   */
  async preparePayment(data: any): Promise<any> {
    try {
      const response = await axios.post(`${API_URL}/auth/prepare-payment`, data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Erreur lors de la préparation du paiement');
      }
      throw error;
    }
  }
  async register(data: GarageRegistration) {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de l\'inscription');
    }
  }


  async resetPassword(data: ResetPasswordData & { code?: string }): Promise<AuthResponse> {
    try {
      const response = await axios.post(`${API_URL}/auth/reset-password`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la réinitialisation du mot de passe');
    }
  }

  async requestPasswordReset(email: string): Promise<any> {
    try {
      const response = await axios.post(`${API_URL}/auth/request-password-reset`, { email });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la demande de réinitialisation');
    }
  }

  async verifyResetCode(email: string, code: string): Promise<any> {
    try {
      const response = await axios.post(`${API_URL}/auth/verify-reset-code`, { email, code });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Code invalide ou expiré');
    }
  }

  async verifyEmail(token: string): Promise<any> {
    try {
      const response = await axios.post(`${API_URL}/auth/verify-email`, { token });

      // Si la vérification réussit, nous pouvons maintenant stocker l'utilisateur
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        this.user = response.data.user;
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la vérification de l\'email');
    }
  }
  async deleteAccountGarage(): Promise<any> {
    try {
      const { garageId } = getAuthInfo();
      const response = await axios.delete(`${API_URL}/auth/garages/${garageId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la demande de réinitialisation');
    }
  }

    async updateNotificationSettings(garageId: string, isNotificationsEnabled: boolean): Promise<any> {
        try {


            const response = await axios.put(
                `${API_URL}/auth/notifications/${garageId}`,
                { isNotificationsEnabled }
            );

            // Mettre à jour les informations de l'utilisateur dans le localStorage
            if (response.data && response.data.success) {
                const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

                // Mettre à jour uniquement le champ isNotificationsEnabled
                const updatedUser = {
                    ...currentUser,
                    isNotificationsEnabled: isNotificationsEnabled
                };

                // Sauvegarder l'objet complet mis à jour
                localStorage.setItem('user', JSON.stringify(updatedUser));
                this.user = updatedUser;
            }

            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Erreur lors de la mise à jour des préférences de notification');
        }
    }
  async resendVerificationEmail(email: string): Promise<any> {
    try {
      const response = await axios.post(`${API_URL}/auth/resend-verification`, { email });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de l\'envoi de l\'email de vérification');
    }
  }

  async updateGarageProfile(userId: string, profileData: GarageProfileUpdateData): Promise<any> {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Non authentifié');
      }

      const response = await axios.put(
          `${API_URL}/auth/profile/${userId}`,
          profileData,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      // Mettre à jour les informations de l'utilisateur dans le localStorage
      if (response.data && response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        this.user = response.data.user;
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la mise à jour du profil');
    }
  }

  logout(): void {
    localStorage.removeItem('user');
    localStorage.clear();
    this.user = null;
  }

  getCurrentUser(): User | null {
    if (this.user) {
      return this.user;
    }
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        this.user = JSON.parse(userStr);
        return this.user;
      } catch (e) {
        localStorage.removeItem('user');
        return null;
      }
    }
    return null;
  }

  isAuthenticated(): boolean {
    const user = this.getCurrentUser();
    // On vérifie non seulement que l'utilisateur existe
    // mais aussi que son email a été vérifié
    return user !== null && user.isActive;
  }
  async registerWithTapPayment(data: any): Promise<any> {
    try {
      const formData = new FormData();

      if (data.logo) {
        formData.append('logo', data.logo);
      }
      if (data.garagePhotos && data.garagePhotos.length > 0) {
        data.garagePhotos.forEach((photo: File) => formData.append('garagePhotos', photo));
      }

      const textFields = [
        'firstName', 'lastName', 'email', 'password', 'phoneNumber',
        'garageName', 'siretNumber', 'garagePhone', 'website', 'description', 'preferredLanguage'
      ];
      textFields.forEach(field => {
        if (data[field] !== undefined && data[field] !== null) {
          formData.append(field, data[field]);
        }
      });

      formData.append('classified', data.classified?.toString() || 'false');
      formData.append('acceptedTerms', data.acceptedTerms?.toString() || 'false');
      formData.append('address', JSON.stringify(data.address));
      formData.append('specialities', JSON.stringify(data.specialities));
      formData.append('vehicleTypes', JSON.stringify(data.vehicleTypes));
      formData.append('businessHours', JSON.stringify(data.businessHours));

      const response = await axios.post(`${API_URL}/auth/tap/register-with-payment`, formData);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Registration error');
      }
      throw error;
    }
  }

  async updateGarageLogo(userId: string, logoFile: File): Promise<any> {
    const formData = new FormData();
    formData.append('logo', logoFile);
    const token = localStorage.getItem('token');
    const response = await axios.put(`${API_URL}/auth/garage/${userId}/logo`, formData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }

  async addGaragePhotos(userId: string, photos: File[], descriptions?: string[]): Promise<any> {
    const formData = new FormData();
    photos.forEach((photo) => formData.append('garagePhotos', photo));
    if (descriptions) {
      descriptions.forEach((desc, i) => formData.append(`photoDescription_${i}`, desc));
    }
    const token = localStorage.getItem('token');
    const response = await axios.post(`${API_URL}/auth/garage/${userId}/photos`, formData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }

  async deleteGaragePhoto(userId: string, photoIndex: number): Promise<any> {
    const token = localStorage.getItem('token');
    const response = await axios.delete(`${API_URL}/auth/garage/${userId}/photos/${photoIndex}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }

  /**
   * Traiter un paiement d'abonnement Tap
   */
  async processTapSubscriptionPayment(paymentData: {
    userId: string;
    isEarlyAdopter: boolean;
    cardData: {
      number: string;
      exp_month: number;
      exp_year: number;
      cvc: string;
      name: string;
    };
    customerData: {
      first_name: string;
      last_name: string;
      email: string;
      phone: string;
    };
    amount: number;
    currency?: string;
    metadata?: Record<string, string>;
  }): Promise<any> {
    try {

      const response = await authAxios.post(`${API_URL}/auth/tap/subscription/payment`, paymentData);
      console.log(response);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors du traitement du paiement Tap');
    }
  }

  /**
   * Traiter un paiement récurrent Tap
   */
  async processTapRecurringPayment(paymentData: {
    userId: string;
    amount: number;
    currency?: string;
    description?: string;
    metadata?: Record<string, string>;
  }): Promise<any> {
    try {
      const { token } = getAuthInfo();
      if (!token) {
        throw new Error('Non authentifié');
      }

      const response = await authAxios.post(`${API_URL}/auth/tap/subscription/recurring`, paymentData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors du paiement récurrent Tap');
    }
  }

  /**
   * Annuler un abonnement Tap
   */
  async cancelTapSubscription(userId: string, immediate = false): Promise<any> {
    try {
      const { token } = getAuthInfo();
      if (!token) {
        throw new Error('Non authentifié');
      }

      const response = await authAxios.post(`${API_URL}/auth/tap/subscription/cancel`, {
        userId,
        immediate
      });

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de l\'annulation de l\'abonnement Tap');
    }
  }

  /**
   * Obtenir les informations de paiement Tap
   */
  async getTapPaymentInfo(userId: string): Promise<any> {
    try {
      const { token } = getAuthInfo();
      if (!token) {
        throw new Error('Non authentifié');
      }

      const response = await authAxios.get(`${API_URL}/auth/tap/payment-info/${userId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des infos de paiement Tap');
    }
  }

  /**
   * Tester la connectivité Tap (pour debug)
   */
  async testTapConnection(): Promise<any> {
    try {
      const response = await axios.get(`${API_URL}/auth/tap/test-connection`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors du test de connexion Tap');
    }
  }

  /**
   * Valider une carte Mada (côté frontend)
   */
   validateMadaCard(cardNumber: string): { isValid: boolean; message?: string } {
    // Supprimer les espaces
    const cleanNumber = cardNumber.replace(/\s/g, '');

    // Vérifier si c'est un numéro Mada valide (commence par 4, 5 ou 6 et a 16 chiffres)
    if (!/^\d{16}$/.test(cleanNumber)) {
      return { isValid: false, message: 'Le numéro de carte doit contenir 16 chiffres' };
    }

    // Les cartes Mada commencent généralement par 4, 5 ou 6
    const firstDigit = cleanNumber[0];
    if (!['4', '5', '6'].includes(firstDigit)) {
      return { isValid: false, message: 'Numéro de carte Mada invalide' };
    }

    // Algorithme de Luhn pour vérifier la validité
    let sum = 0;
    let shouldDouble = false;

    for (let i = cleanNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cleanNumber[i]);

      if (shouldDouble) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }

      sum += digit;
      shouldDouble = !shouldDouble;
    }

    const isLuhnValid = sum % 10 === 0;

    if (!isLuhnValid) {
      return { isValid: false, message: 'Numéro de carte invalide' };
    }

    return { isValid: true };
  }

  /**
   * Formater un numéro de carte pour l'affichage (avec espaces)
   */
   formatCardNumber(cardNumber: string): string {
    const cleaned = cardNumber.replace(/\D/g, '');
    const formatted = cleaned.replace(/(\d{4})/g, '$1 ').trim();
    return formatted.substring(0, 19); // Limite à 16 chiffres + 3 espaces
  }

  /**
   * Formater une date d'expiration MM/YY
   */
   formatExpiryDate(expiryDate: string): string {
    const cleaned = expiryDate.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.substring(0, 2) + '/' + cleaned.substring(2, 4);
    }
    return cleaned;
  }

  /**
   * Valider une date d'expiration
   */
   validateExpiryDate(expiryDate: string): { isValid: boolean; message?: string } {
    const parts = expiryDate.split('/');
    if (parts.length !== 2) {
      return { isValid: false, message: 'Format invalide (utilisez MM/YY)' };
    }

    const month = parseInt(parts[0]);
    const year = parseInt(parts[1]);

    if (month < 1 || month > 12) {
      return { isValid: false, message: 'Mois invalide' };
    }

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear() % 100;
    const currentMonth = currentDate.getMonth() + 1;

    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      return { isValid: false, message: 'Carte expirée' };
    }

    return { isValid: true };
  }

  /**
   * Formater un numéro de téléphone saoudien
   */
   formatSaudiPhone(phone: string): string {
    const cleaned = phone.replace(/\D/g, '');

    // Si commence par 966, formater comme +966 XX XXX XXXX
    if (cleaned.startsWith('966')) {
      const number = cleaned.substring(3);
      if (number.length === 9) {
        return `+966 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
      }
    }

    // Si commence par 0, supprimer le 0 et ajouter +966
    if (cleaned.startsWith('0') && cleaned.length === 10) {
      const number = cleaned.substring(1);
      return `+966 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
    }

    // Si 9 chiffres, ajouter +966
    if (cleaned.length === 9) {
      return `+966 ${cleaned.substring(0, 2)} ${cleaned.substring(2, 5)} ${cleaned.substring(5)}`;
    }

    return phone; // Retourner tel quel si format non reconnu
  }
}




export const authService = new AuthService();